/**
 * The tournament's own tables — FR-TOUR-5, FR-TOUR-7, BR-TOUR-1, BR-TOUR-2.
 *
 * Three tables, in the order the club will read them: who is winning, which
 * partnership is carrying it, and how each player did. All three rank the
 * same way — a flat point per win, ties broken on score difference — because
 * that is the rule the tournament was announced under.
 *
 * This is deliberately **not** the session Standings table. That one ranks on
 * Match Points, where a heavy win is worth more than a narrow one, and showing
 * both rules on one screen would be showing two answers to "who is winning".
 * The session table is still there under its own tab for anyone who wants a
 * player's ordinary record; this is the tournament's scoreboard.
 *
 * The team table is the headline and reads as a scoreboard rather than a
 * spreadsheet: two rows, large figures, the leader in cork. Everything with
 * many rows is a table with a sticky name column, as every other table here is.
 */

import { useState } from "react";
import {
  api,
  type Player,
  type TeamStanding,
  type TournamentPairStanding,
  type TournamentPlayerStanding,
  type TournamentRow,
  type TournamentView,
} from "../api.js";
import { PRIMARY_CLASS, SORTED_DESC, rankText } from "../statsPresentation.js";

type Which = "teams" | "pairs" | "players";

export function TournamentTable({
  tournament,
  isAdmin,
  onChanged,
}: {
  tournament: TournamentView;
  isAdmin: boolean;
  onChanged: () => Promise<void>;
}): React.JSX.Element {
  const [which, setWhich] = useState<Which>("teams");
  const { teams, pairs, players } = tournament.standings;

  return (
    <>
      <TeamScoreboard tournament={tournament} />

      <div className="row subnav">
        {(
          [
            ["teams", "Teams"],
            ["pairs", "Pairs"],
            ["players", "Players"],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            className={which === id ? "chip chip-on" : "chip"}
            onClick={() => setWhich(id)}
          >
            {label}
          </button>
        ))}
      </div>

      {which === "teams" ? (
        <TournamentRows
          title="Teams"
          nameHeader="Team"
          provisional={tournament.provisional}
          rows={teams.map((t) => ({ key: t.teamId, label: t.name, sub: null, row: t }))}
          leadingKeys={teams.filter((t) => t.rank === 1 && t.played > 0).map((t) => t.teamId)}
        />
      ) : null}

      {which === "pairs" ? (
        <TournamentRows
          title="Pairs"
          nameHeader="Partnership"
          provisional={tournament.provisional}
          rows={pairs.map((p) => ({
            key: p.pairCode,
            label: p.label,
            sub: `${p.pairCode.replace("/", " & ")}`,
            row: p,
          }))}
          leadingKeys={pairs.filter((p) => p.rank === 1).map((p) => p.pairCode)}
          empty="No games logged yet, so no partnership has a record."
        />
      ) : null}

      {which === "players" ? (
        <TournamentRows
          title="Players"
          nameHeader="Player"
          provisional={tournament.provisional}
          rows={players.map((p) => ({
            key: p.code,
            label: p.displayName,
            sub: p.code,
            row: p,
          }))}
          leadingKeys={players.filter((p) => p.rank === 1).map((p) => p.code)}
          empty="No games logged yet."
        />
      ) : null}

      <Draw tournament={tournament} isAdmin={isAdmin} onChanged={onChanged} />

      {isAdmin ? <CountsToggle tournament={tournament} onChanged={onChanged} /> : null}
    </>
  );
}

/**
 * The headline: two teams, the score between them, and who is ahead.
 *
 * A two-row table would be technically the same information and would read as
 * an afterthought. This is the number people will hold their phone up to show
 * each other, so it gets the space.
 */
function TeamScoreboard({ tournament }: { tournament: TournamentView }): React.JSX.Element {
  const { teams, leadingTeamCodes } = tournament.standings;
  const anyPlayed = teams.some((t) => t.played > 0);

  return (
    <section className="matchup scoreboard">
      <h3 className="card-head">
        {tournament.name}
        {tournament.provisional ? <span className="provisional-flag">provisional</span> : null}
      </h3>

      <div className="scoreboard-rows">
        {teams.map((team) => (
          <div
            key={team.teamId}
            className={leadingTeamCodes.includes(team.code) ? "scoreline lead" : "scoreline"}
          >
            <span className="scoreline-name">
              {team.name}
              <em>
                {team.wins}–{team.losses}
                {team.played > 0 ? ` · ${signed(team.pointDiff)} pts` : ""}
              </em>
            </span>
            <span className="scoreline-points num">{team.points}</span>
          </div>
        ))}
      </div>

      <p className="resting">
        {anyPlayed
          ? verdict(teams, leadingTeamCodes, tournament)
          : `${tournament.fixtureCount} games, ${tournament.roundCount} rounds. One point a win; a tie is broken on score difference.`}
      </p>
    </section>
  );
}

/** What the table actually says, in a sentence, rather than left to be read off. */
function verdict(
  teams: readonly TeamStanding[],
  leadingTeamCodes: readonly string[],
  tournament: TournamentView,
): string {
  const leaders = teams.filter((t) => leadingTeamCodes.includes(t.code));
  const remaining = tournament.fixtureCount - tournament.playedCount;
  const finished = remaining === 0;

  if (leaders.length > 1) {
    const [a, b] = leaders;
    const separator =
      a.pointDiff === b.pointDiff
        ? "level on points and on score difference"
        : "level on points";
    return finished
      ? `${leaders.map((t) => t.name).join(" and ")} finish ${separator}.`
      : `${leaders.map((t) => t.name).join(" and ")} are ${separator}, with ${remaining} games left.`;
  }

  const [leader, other] = [leaders[0], teams.find((t) => t.code !== leaders[0]?.code)];
  if (!leader || !other) return "";
  const margin = leader.points - other.points;

  if (finished) {
    return margin === 0
      ? `${leader.name} take it on score difference.`
      : `${leader.name} win by ${margin} ${margin === 1 ? "point" : "points"}.`;
  }
  return margin === 0
    ? `${leader.name} lead on score difference, with ${remaining} games left.`
    : `${leader.name} lead by ${margin}, with ${remaining} games left.`;
}

/**
 * One ranked table. Same columns for teams, pairs and players, because they
 * are the same question asked of three different things and three different
 * column sets would invite three different readings.
 *
 * P is the tournament points — the emphasised column, for the same reason MP
 * is emphasised on the session table: it is what the event is decided on. Diff
 * sits next to it because it is the tiebreak, and a tiebreak you cannot see is
 * an argument waiting to happen.
 */
function TournamentRows({
  title,
  nameHeader,
  provisional,
  rows,
  leadingKeys,
  empty,
}: {
  title: string;
  nameHeader: string;
  provisional: boolean;
  rows: Array<{ key: string; label: string; sub: string | null; row: TournamentRow }>;
  leadingKeys: string[];
  empty?: string;
}): React.JSX.Element {
  if (rows.length === 0) {
    return (
      <section className="block">
        <h3 className="card-head">{title}</h3>
        <p>{empty ?? "Nothing to show yet."}</p>
      </section>
    );
  }

  return (
    <section className="block">
      <h3 className="card-head">
        {title}
        {provisional ? <span className="provisional-flag">provisional</span> : null}
      </h3>

      <div className="table-scroll">
        <table className="standings">
          <thead>
            <tr>
              <th scope="col" className="rank" aria-label="Rank">
                #
              </th>
              <th scope="col" className="sticky-col">
                {nameHeader}
              </th>
              <th scope="col">G</th>
              <th scope="col">W</th>
              <th scope="col">L</th>
              <th scope="col">PW</th>
              <th scope="col">PL</th>
              <th scope="col">Diff</th>
              <th scope="col" className={PRIMARY_CLASS} aria-sort="descending">
                P<span className="sortmark">{SORTED_DESC}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ key, label, sub, row }) => (
              <tr key={key} className={leadingKeys.includes(key) ? "leader" : undefined}>
                <td className="rank">{rankText(row.rank, row.shared)}</td>
                <th scope="row" className="sticky-col">
                  {label}
                  {sub ? <span className="guest-flag">{sub}</span> : null}
                </th>
                <td>{row.played}</td>
                <td>{row.wins}</td>
                <td>{row.losses}</td>
                <td>{row.pointsWon}</td>
                <td>{row.pointsLost}</td>
                <td className={row.pointDiff === 0 ? undefined : row.pointDiff > 0 ? "pos" : "neg"}>
                  {signed(row.pointDiff)}
                </td>
                <td className={PRIMARY_CLASS}>{row.points}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="note">
        P is tournament points — one a win. Ties are broken on Diff, the score
        difference across every game played.
      </p>
    </section>
  );
}

/**
 * FR-TOUR-4a — who holds each place in the draw, and how to change it.
 *
 * This is where a no-show is dealt with, and it is deliberately here rather
 * than on the Play screen: it is a decision taken once, by an admin, before
 * anybody is standing on a court, and the entry path has no modes in it on
 * purpose (FR-ENTRY-4a).
 */
function Draw({
  tournament,
  isAdmin,
  onChanged,
}: {
  tournament: TournamentView;
  isAdmin: boolean;
  onChanged: () => Promise<void>;
}): React.JSX.Element {
  const [replacing, setReplacing] = useState<string | null>(null);
  const [roster, setRoster] = useState<Player[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const inDraw = new Set(tournament.teams.flatMap((t) => t.members).map((m) => m.playerId));

  function openReplace(code: string): void {
    setError(null);
    setReplacing(code);
    if (!roster) {
      api
        .players()
        .then(setRoster)
        .catch(() => setError("Could not load the roster."));
    }
  }

  return (
    <section className="block">
      <h3 className="card-head">The draw</h3>

      {tournament.teams.map((team) => (
        <div key={team.id} className="drawteam">
          <div className="eyebrow">{team.name}</div>
          {team.members.map((member) => (
            <div key={member.code} className="rotrow">
              <span className="who">
                <b>{member.code}</b> {member.displayName}
              </span>
              {isAdmin ? (
                <button className="mini" onClick={() => openReplace(member.code)}>
                  Replace
                </button>
              ) : null}
            </div>
          ))}
        </div>
      ))}

      {replacing ? (
        <div className="swap-strip">
          <div className="strip-label">
            Who is playing as <b>{replacing}</b>?
          </div>
          <div className="strip-options">
            {roster === null ? (
              <span className="muted small">Loading…</span>
            ) : (
              roster
                .filter((p) => !inDraw.has(p.id))
                .map((p) => (
                  <button
                    key={p.id}
                    className="chip"
                    onClick={() => {
                      api
                        .substituteTournamentMember(tournament.id, replacing, p.id)
                        .then(() => {
                          setReplacing(null);
                          return onChanged();
                        })
                        .catch((e: unknown) =>
                          setError(e instanceof Error ? e.message : "Could not replace them."),
                        );
                    }}
                  >
                    {p.displayName}
                  </button>
                ))
            )}
            <button className="chip chip-warn" onClick={() => setReplacing(null)}>
              Cancel
            </button>
          </div>
          {error ? <p className="error">{error}</p> : null}
        </div>
      ) : null}

      <p className="hint">
        Replacing somebody changes every game they have not played yet. Games
        already logged keep whoever actually played them.
      </p>
    </section>
  );
}

/** FR-STAT-10 — the club's all-time figures, in or out. Off by default. */
function CountsToggle({
  tournament,
  onChanged,
}: {
  tournament: TournamentView;
  onChanged: () => Promise<void>;
}): React.JSX.Element {
  const [busy, setBusy] = useState(false);

  return (
    <section className="block">
      <h3 className="card-head">All-time figures</h3>
      <p className="muted small">
        {tournament.countsTowardAlltime
          ? "These games count towards every all-time figure, like any session."
          : "These games stay in the tournament and out of the club's all-time figures."}
      </p>
      <button
        className="quiet"
        disabled={busy}
        onClick={() => {
          setBusy(true);
          void api
            .setTournamentCountsTowardAlltime(tournament.id, !tournament.countsTowardAlltime)
            .then(onChanged)
            .finally(() => setBusy(false));
        }}
      >
        {tournament.countsTowardAlltime ? "Keep them out of all-time" : "Count them in all-time"}
      </button>
    </section>
  );
}

function signed(value: number): string {
  return value > 0 ? `+${value}` : String(value);
}

export type { TournamentPairStanding, TournamentPlayerStanding };
